import { Component, OnInit, Input, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CtaImage, CtaButton, CtaTooltip } from './campaign-cta-interface';
import { CAMPAIGN_CTA_CONSTANTS } from './campaign-cta.constants';
import { TooltipPosition } from '@finder/shared/components/tooltip/tooltip.interface';
import { CommonModule } from '@angular/common';
import { PictureComponent } from '../picture/picture.component';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { IconTooltipComponent } from '../icon-tooltip/icon-tooltip.component';
import { ButtonComponent } from '../button/button.component';

@Component({
    selector: 'finder-campaign-cta',
    templateUrl: './campaign-cta.component.html',
    styleUrls: ['./campaign-cta.component.scss'],
    standalone: true,
    imports: [
        ButtonComponent,
        CommonModule,
        ComponentHeaderComponent,
        IconTooltipComponent,
        PictureComponent,
        SafeHtmlPipe,
    ]
})
export class CampaignCtaComponent implements OnInit, OnChanges {
    @Input() ctaData: any;

    image: Partial<CtaImage> = {};
    button: CtaButton;
    tooltip: CtaTooltip;
    formattedText: string;
    showButton = false;
    isMobile = false;
    hasImage = false;
    hasBgImage = false;
    gradient = false;
    cssClasses: string;
    logo: any;
    buttonAlignment: string;
    textAlignment: string;
    private price: string;

    constructor(
        private viewportDetection: ViewportDetectionService,
        private el: ElementRef
    ) {}

    ngOnInit() {
        this.init();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.ctaData.firstChange) {
            return;
        }

        // Reset
        this.image = {};
        this.button = void 0;
        this.tooltip = void 0;
        this.formattedText = void 0;
        this.showButton = false;
        this.isMobile = false;
        this.hasImage = false;
        this.hasBgImage = false;
        this.gradient = false;
        this.cssClasses = void 0;
        this.logo = void 0;
        this.buttonAlignment = void 0;
        this.price = void 0;

        this.ctaData = changes.ctaData.currentValue;
        this.init();
    }

    private init() {
        this.isMobile = this.viewportDetection.isMobile();
        this.showButton = !!this.ctaData.button;
        this.gradient = this.ctaData.gradient;
        this.setImage();
        this.setFormattedText();
        this.setTooltip();
        this.setButton();
        this.setCssClasses();
        this.setLogo();
    }

    private setImage() {
        if (!this.ctaData.image) {
            return;
        }

        this.hasBgImage = this.ctaData.image.placement === 'bg';
        this.hasImage = !this.hasBgImage;

        this.image = this.ctaData.image;
    }

    private setFormattedText() {
        if (!this.ctaData.price) {
            this.formattedText = this.ctaData.text;

            return;
        }

        this.price = this.ctaData.currency + this.ctaData.price;
        this.formattedText = this.ctaData.text.replace(CAMPAIGN_CTA_CONSTANTS.pricePlaceholder, this.price);
    }

    private setTooltip() {
        const tooltip = get(this.ctaData, 'tooltip', {});
        this.tooltip = {
            disable: tooltip.disable || isEmpty(tooltip) ? true : false,
            text: tooltip.text,
            title: tooltip.title,
            position: TooltipPosition.top,
        };
    }

    private setButton() {
        if (!this.ctaData.button) {
            return;
        }

        this.button = {
            link: this.ctaData.button.link,
            linkId: this.ctaData.button.linkId,
            target: '_self',
            label: this.ctaData.button.label,
            theme: this.ctaData.button.theme,
        };

        // Button class
        this.buttonAlignment = this.ctaData.button.alignment ?? '';
        this.textAlignment = this.ctaData.textAlignment;
    }

    private setCssClasses() {
        this.cssClasses = get(this.ctaData, 'style', '');
        if (this.showButton === false) {
            this.cssClasses += ' no-button';
        }

        if (this.hasImage) {
            this.cssClasses += ' has-image';
            this.cssClasses += (this.image.placement === 'right') ? ' right' : ' left';
        }

        this.cssClasses = this.cssClasses.trim();
    }

    private setLogo() {
        if (!this.ctaData.logo) {
            return;
        }

        this.logo = this.ctaData.logo;
    }
}
